import React, { useEffect, useState, useMemo } from 'react';
import Pagination from '../components/Pagination';
import API from '../api';
import './services.css';
import exampleCsv from '../assets/service.csv';
import { useNavigate } from 'react-router-dom';

function Services() {
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    duration_minutes: '',
    description: '',
  });
  const [editingId, setEditingId] = useState(null);

  const [selected, setSelected] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [search, setSearch] = useState('');
  const [svcPage, setSvcPage]         = useState(1);
  const [svcPageSize, setSvcPageSize] = useState(10);

  const role = localStorage.getItem('role');
  const isAdmin = role === 'admin';

  const navigate = useNavigate();

  const handleBookingRedirect = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const empBusinessId = localStorage.getItem('id');
    const businessId = userData?.user?.id || userData?.id;

    // if (isAdmin) {
    //   businessId ? navigate(`/booking/${businessId}`) : alert('Business ID not found.');
    // } else {
    //   empBusinessId ? navigate(`/booking/${empBusinessId}`) : alert('Employee Business ID not found.');
    // }

    if (isAdmin) {
      if (businessId) {
        window.open(`/booking/${businessId}`, "_blank"); // ← NEW TAB
      } else {
        alert('Business ID not found.');
      }
    } else {
      if (empBusinessId) {
        window.open(`/booking/${empBusinessId}`, "_blank"); // ← NEW TAB
      } else {
        alert('Employee Business ID not found.');
      }
    }
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const id = localStorage.getItem('id'); // admin & employee
        const res = await API.get(`/auth/services/business/${id}`);
        setServices(res.data);
        setError(null);
      } catch (err) {
        if (err.response?.status === 404) {
          setServices([]);
          // setError('No services found for your business.');
        } else {
          setError('Failed to load services.');
        }
      }
    };
    fetchServices();
  }, []);

  // ---------- CSV ----------
  const handleCsvSelect = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      alert('Please select a .csv file');
      return;
    }
    setCsvFile(file);
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      alert('Please choose a CSV file.');
      return;
    }
    const body = new FormData();
    body.append('file', csvFile);
    body.append('type', 'services');

    try {
      await API.post('/auth/uploadCsv', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('CSV uploaded successfully!');
      setShowCsvModal(false);
      setCsvFile(null);

      // refresh
      const id = localStorage.getItem('id');
      const res = await API.get(`/auth/services/business/${id}`);
      setServices(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error uploading CSV';
      alert(msg);
    }
  };

  // ---------- CRUD ----------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'duration_minutes' && value !== '' && Number(value) % 5 !== 0) return;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        business_id: localStorage.getItem('id'),
      };

      if (editingId) {
        await API.put(`/auth/services/${editingId}`, payload);
      } else {
        await API.post('/auth/postService', payload);
      }

      // refresh + close
      const id = localStorage.getItem('id');
      const res = await API.get(`/auth/services/business/${id}`);
      setServices(res.data);

      setShowFormModal(false);
      setEditingId(null);
      setFormData({ name: '', price: '', duration_minutes: '', description: '' });
    } catch (err) {
      alert('Failed to save service: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (service) => {
    setFormData({
      name: service.name ?? '',
      price: service.price ?? '',
      duration_minutes: service.duration_minutes ?? '',
      description: service.description ?? '',
    });
    setEditingId(service._id);
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await API.delete(`/auth/services/${id}`);
        setServices((prev) => prev.filter((s) => s._id !== id));
      } catch (err) {
        alert('Failed to delete service: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const toggleAll = (e) => {
    if (e.target.checked) setSelected(services.map((s) => s._id));
    else setSelected([]);
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (window.confirm('Delete selected services?')) {
      try {
        await API.post('/auth/services/bulk-delete', { ids: selected });
        setServices((prev) => prev.filter((s) => !selected.includes(s._id)));
        setSelected([]);
      } catch (err) {
        alert('Failed to delete services: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  // New: Edit button (above the table) logic
  const handleEditSelected = () => {
    if (selected.length === 0) {
      alert('Please select exactly one service to edit.');
      return;
    }
    if (selected.length > 1) {
      alert('Only one service can be edited at a time.');
      return;
    }
    const svc = services.find((s) => s._id === selected[0]);
    if (!svc) {
      alert('Selected service not found.');
      return;
    }
    handleEdit(svc);
  };

  const filteredServices = useMemo(() => services.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    (s.description || '').toLowerCase().includes(search.toLowerCase())
  ), [services, search]);
  useEffect(() => setSvcPage(1), [search]);
  const paginatedServices = useMemo(() =>
    filteredServices.slice((svcPage - 1) * svcPageSize, svcPage * svcPageSize),
  [filteredServices, svcPage, svcPageSize]);

  return (
    <div className="services-page">
      {/* Header */}
      <div className="sp-header">
        <div className="sp-header-left">
          <h1 className="sp-title">All Services <span className="sp-count">{services.length}</span></h1>
          <input
            className="sp-search"
            type="text"
            placeholder="Search services…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {isAdmin && (
          <div className="sp-actions">
            <button className="sp-btn sp-btn-ghost" onClick={handleBookingRedirect}>
              Go to Booking Page
            </button>

            <button
              className="sp-btn sp-btn-outline"
              onClick={handleEditSelected}
              title="Edit selected service"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                />
              </svg>
              Edit Service
            </button>

            {selected.length > 0 && (
              <button className="sp-btn sp-btn-danger-outline" onClick={handleBulkDelete}>
                Delete Selected
              </button>
            )}

            <button
              className="sp-btn sp-btn-primary sp-btn-icon"
              onClick={() => {
                setShowFormModal(true);
                setFormData({ name: '', price: '', duration_minutes: '', description: '' });
                setEditingId(null);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 5v14m-7-7h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Add Service
            </button>

            <button className="sp-btn sp-btn-lime" onClick={() => setShowCsvModal(true)}>
              Update CSV
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {error ? (
        <p style={{ color: 'red', marginTop: 12 }}>{error}</p>
      ) : (
        <div className="sp-table-wrap">
          <table className="sp-table">
            <thead>
              <tr>
                {isAdmin && (
                  <th className="sp-checkbox">
                    <input type="checkbox" onChange={toggleAll} />
                  </th>
                )}
                <th>Service Name</th>
                <th>Duration (Min)</th>
                <th>Price ($)</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="sp-empty">
                    {search ? 'No services match your search.' : 'No services yet.'}
                  </td>
                </tr>
              )}
              {paginatedServices.map((service) => (
                <tr key={service._id}>
                  {isAdmin && (
                    <td className="sp-checkbox">
                      <input
                        type="checkbox"
                        checked={selected.includes(service._id)}
                        onChange={() => toggleSelect(service._id)}
                      />
                    </td>
                  )}
                  <td className="sp-name-cell">{service.name}</td>
                  <td><span className="sp-badge sp-badge--duration">⏱ {service.duration_minutes} min</span></td>
                  <td><span className="sp-badge sp-badge--price">${parseFloat(service.price).toFixed(2)}</span></td>
                  <td className="sp-desc-cell">{service.description ? service.description : <span className="sp-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={svcPage}
            totalPages={Math.max(1, Math.ceil(filteredServices.length / svcPageSize))}
            totalItems={filteredServices.length}
            pageSize={svcPageSize}
            onPageChange={setSvcPage}
            onPageSizeChange={(s) => { setSvcPageSize(s); setSvcPage(1); }}
          />
        </div>
      )}

      {/* CSV Modal */}
      {showCsvModal && isAdmin && (
        <div
          className="sp-modal-overlay"
          onClick={(e) => e.target.classList.contains('sp-modal-overlay') && setShowCsvModal(false)}
        >
          <div className="sp-modal">
            <div className="sp-modal-header">
              <h3>Upload CSV File</h3>
              <button className="sp-modal-close" onClick={() => setShowCsvModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleCsvUpload}>
              <div
                className={`sp-dropzone ${isDragging ? 'is-dragging' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleCsvSelect(e.dataTransfer.files?.[0]);
                }}
              >
                <div className="sp-cloud">☁️</div>
                <p>Drag &amp; drop your file here or</p>
                <label className="sp-browse">
                  Browse files
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleCsvSelect(e.target.files?.[0])}
                    hidden
                  />
                </label>
                {csvFile && <div className="sp-file-name">{csvFile.name}</div>}
              </div>

              <div className="sp-modal-actions">
                <button
                  type="button"
                  className="sp-btn sp-btn-light"
                  onClick={() => {
                    setShowCsvModal(false);
                    setCsvFile(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="sp-btn sp-pop-up-btn-primary">
                  Upload
                </button>
              </div>
            </form>

            <a className="sp-example" href={exampleCsv} download>
              ⬇️ Download Example CSV
            </a><br></br>
            <p style={{ textAlign: "center" }}>Get Your Business Id from user profile</p>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showFormModal && isAdmin && (
        <div
          className="sp-modal-overlay"
          onClick={(e) => e.target.classList.contains('sp-modal-overlay') && setShowFormModal(false)}
        >
          <div className="sp-modal">
            <div className="sp-modal-header">
              <h3>{editingId ? 'Edit Service' : 'Add New Service'}</h3>
              <button className="sp-modal-close" onClick={() => setShowFormModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <label className="sp-label">Service Name</label>
              <input
                className="sp-input"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />

              <label className="sp-label">Price ($)</label>
              <input
                className="sp-input"
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
              />

              <label className="sp-label">Duration ( Minutes – Multiple of 5 )</label>
              <input
                className="sp-input"
                type="number"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleInputChange}
                required
                min="5"
                step="5"
              />

              <label className="sp-label">Service Description</label>
              <textarea
                className="sp-textarea"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Optional short description"
                rows={3}
              />

              <div className="sp-modal-actions">
                <button
                  type="button"
                  className="sp-btn sp-btn-light"
                  onClick={() => setShowFormModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="sp-btn sp-pop-up-btn-primary">
                  {editingId ? 'Save' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services;
